import { ProBadge } from "@/components/ProBadge";
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Watch, Copy, CheckCircle, LinkBreak, AppleLogo, GoogleLogo, DeviceMobile, CaretDown } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProLock } from "@/components/ProGate";
import { isNativeApp, healthLabel, connectHealth } from "@/lib/health";

const ICONS = { native: DeviceMobile, apple: AppleLogo, oura: Watch, googlehealth: GoogleLogo, whoop: Watch, strava: Watch };

export default function WearablesCard() {
  const { user } = useAuth();
  const native = isNativeApp();
  const [providers, setProviders] = useState(null);
  const [ouraOpen, setOuraOpen] = useState(false);
  const [appleOpen, setAppleOpen] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [appleUrl, setAppleUrl] = useState("");
  const [ouraToken, setOuraToken] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/wearables/providers").then(({ data }) => setProviders(data.providers)).catch(() => setProviders([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("connected")) toast.success(`${q.get("connected")} connected — your check-ins will auto-fill`);
    if (q.get("wearable_error")) toast.error(`Could not connect ${q.get("wearable_error")} — try again`);
    if (q.get("connected") || q.get("wearable_error")) {
      window.history.replaceState({}, "", "/app?tab=profile");
    }
  }, []);

  if (!(user?.entitlement?.is_pro)) {
    return <ProLock testId="wearables-locked" feature="Wearable Integrations" desc="Auto-fill your check-ins from Oura, Google Health or Apple Health. Your 7-day Pro trial has ended." />;
  }

  const connect = async (p) => {
    if (p.id === "oura") return setOuraOpen(true);
    if (p.id === "native") {
      setBusy(true);
      try {
        const data = await connectHealth();
        const got = Object.keys(data).length;
        toast.success(got ? `${healthLabel()} connected — ${got} metrics synced` : `${healthLabel()} connected — no data yet, wear your device tonight`);
        load();
      } catch (e) {
        toast.error(e?.message || `Could not connect ${healthLabel()}`);
      } finally {
        setBusy(false);
      }
      return;
    }
    if (p.id === "apple") {
      const { data } = await api.post("/wearables/apple/enable");
      setAppleUrl(data.webhook_url);
      setAppleOpen(true);
      load();
      return;
    }
    try {
      const { data } = await api.get(`/wearables/${p.id}/authorize`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const disconnect = async (p) => {
    await api.post(`/wearables/${p.id}/disconnect`);
    toast.success(`${p.name} disconnected`);
    load();
  };

  const saveOura = async () => {
    setBusy(true);
    try {
      await api.post("/wearables/oura/connect", { token: ouraToken });
      toast.success("Oura connected — sleep, HRV and steps will auto-fill");
      setOuraOpen(false);
      setOuraToken("");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const list = providers || [];
  const mainList = list.filter((p) => p.id !== "apple" && !(p.id === "native" && !native && !p.connected));
  const nativeRow = list.find((p) => p.id === "native");
  const appleRow = list.find((p) => p.id === "apple");

  const Row = ({ p }) => {
    const Icon = ICONS[p.id] || Watch;
    const label = p.id === "native" ? healthLabel() : p.name;
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-border px-4 py-3" data-testid={`wearable-${p.id}`}>
        <Icon size={20} weight="fill" color={p.connected ? "var(--brand)" : "#7C8AA5"} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium flex items-center gap-2">
            {label}
            {p.connected && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand" data-testid={`wearable-${p.id}-connected`}>
                <CheckCircle size={12} weight="fill" /> Connected
              </span>
            )}
          </div>
          {p.note && !p.connected && <div className="text-xs text-muted2">{p.note}</div>}
          {p.connected && p.last_sync && (
            <div className="text-xs text-muted2">Last sync {new Date(p.last_sync).toLocaleString()}</div>
          )}
        </div>
        {p.connected ? (
          <div className="flex items-center gap-2">
            {p.id === "apple" && (
              <button data-testid="apple-setup-btn" onClick={() => connect(p)}
                className="text-xs font-medium border border-border rounded-full px-3 py-1.5 hover:bg-secondary transition-colors">
                Setup
              </button>
            )}
            <button data-testid={`wearable-${p.id}-disconnect-btn`} onClick={() => disconnect(p)}
              className="text-xs font-medium text-muted2 hover:text-ink inline-flex items-center gap-1 transition-colors">
              <LinkBreak size={13} /> Disconnect
            </button>
          </div>
        ) : (
          <button
            data-testid={`wearable-${p.id}-connect-btn`}
            onClick={() => connect(p)}
            disabled={!p.available || (busy && p.id === "native")}
            className="text-xs font-medium bg-ink text-bg rounded-full px-4 py-2 hover:bg-brand transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy && p.id === "native" ? "Connecting…" : "Connect"}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="clay p-6 sm:p-8" data-testid="wearables-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-brand/10 flex items-center justify-center">
          <Watch size={20} weight="fill" color="var(--brand)" />
        </div>
        <div>
          <h3 className="font-display text-lg font-medium flex items-center gap-2">Connected devices <ProBadge testId="pro-badge-wearables" /></h3>
          <p className="text-xs text-muted2">Your wearable auto-fills sleep, HRV, resting HR and steps at check-in.</p>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {providers === null ? (
          <p className="text-sm text-muted2">Loading…</p>
        ) : (
          mainList.map((p) => <Row key={p.id} p={p} />)
        )}
      </div>

      {/* Web: Apple Health / Health Connect need the native app */}
      {!native && providers !== null && (
        <div className="mt-4 rounded-2xl bg-secondary/60 px-4 py-3.5" data-testid="native-app-note">
          <div className="flex items-start gap-3">
            <DeviceMobile size={18} weight="fill" color="var(--brand)" className="mt-0.5 shrink-0" />
            <div className="text-xs text-muted2">
              <span className="text-sm font-medium text-ink block">Apple Health &amp; Health Connect</span>
              These live on your phone, so they connect with a single permission tap inside the Ikavio app — coming to the App Store and Play Store. No setup, no webhooks.
            </div>
          </div>
          <button
            data-testid="advanced-toggle-btn"
            onClick={() => setAdvanced((v) => !v)}
            className="mt-3 inline-flex items-center gap-1 text-[11px] font-medium text-muted2 hover:text-ink transition-colors"
          >
            <CaretDown size={11} weight="bold" className={`transition-transform ${advanced ? "rotate-180" : ""}`} />
            {advanced ? "Hide advanced setup" : "Advanced: connect Apple Health today without the app"}
          </button>
          {advanced && appleRow && (
            <div className="mt-3" data-testid="advanced-apple-block">
              <Row p={appleRow} />
              <p className="text-[11px] text-muted2 mt-2">
                Power-user route: a free iPhone app (Health Auto Export) pushes your Health data to a private Ikavio URL. Takes ~3 minutes.
              </p>
            </div>
          )}
        </div>
      )}

      {native && nativeRow && !nativeRow.connected && (
        <p className="mt-4 text-xs text-muted2" data-testid="native-hint">
          Tap Connect and allow {healthLabel()} access — Ikavio reads sleep, HRV, resting heart rate and steps only, and never writes to your health data.
        </p>
      )}

      {/* Oura token dialog */}
      <Dialog open={ouraOpen} onOpenChange={setOuraOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="oura-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-light text-xl">Connect Oura Ring</DialogTitle>
            <DialogDescription>
              Create a Personal Access Token at{" "}
              <a href="https://cloud.ouraring.com/personal-access-tokens" target="_blank" rel="noreferrer" className="text-brand underline">
                cloud.ouraring.com/personal-access-tokens
              </a>{" "}
              and paste it below.
            </DialogDescription>
          </DialogHeader>
          <input
            data-testid="oura-token-input"
            type="password"
            value={ouraToken}
            onChange={(e) => setOuraToken(e.target.value)}
            placeholder="Personal access token"
            className="w-full bg-surface border border-border rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand"
          />
          <button
            data-testid="oura-save-btn"
            onClick={saveOura}
            disabled={busy || !ouraToken.trim()}
            className="w-full bg-brand text-white rounded-full py-3 text-sm font-medium hover:bg-ink transition-colors disabled:opacity-50"
          >
            {busy ? "Verifying…" : "Connect Oura"}
          </button>
        </DialogContent>
      </Dialog>

      {/* Apple Health setup dialog (advanced webhook route) */}
      <Dialog open={appleOpen} onOpenChange={setAppleOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="apple-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-light text-xl">Connect Apple Health</DialogTitle>
            <DialogDescription>Your iPhone pushes Health data to Ikavio automatically — set it up once:</DialogDescription>
          </DialogHeader>
          <ol className="text-sm space-y-2.5 list-decimal pl-5 text-ink/85">
            <li>Install <span className="font-medium">Health Auto Export</span> from the App Store</li>
            <li>Create a new <span className="font-medium">REST API</span> automation</li>
            <li>Paste your personal webhook URL below as the endpoint</li>
            <li>Select metrics: <span className="font-medium">Sleep, HRV, Resting Heart Rate, Steps</span> and enable auto-sync</li>
          </ol>
          <div className="flex items-center gap-2 bg-secondary rounded-xl px-3 py-2.5">
            <code className="text-[11px] break-all flex-1" data-testid="apple-webhook-url">{appleUrl}</code>
            <button
              data-testid="apple-copy-url-btn"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(appleUrl);
                  toast.success("Webhook URL copied");
                } catch {
                  toast.error("Copy failed — select the URL manually");
                }
              }}
              className="shrink-0 text-muted2 hover:text-ink transition-colors"
            >
              <Copy size={16} weight="bold" />
            </button>
          </div>
          <p className="text-xs text-muted2">Keep this URL private — it's unique to your account.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
